import React, { useState, useEffect } from 'react'
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'

interface RefundModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (amountCents: number) => Promise<void>
  orderTotalCents: number
  maxRefundCents: number
  isLoading?: boolean
  mode?: 'PARTIAL' | 'FULL'
  fixedAmountCents?: number
}

const RefundModal: React.FC<RefundModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  orderTotalCents,
  maxRefundCents,
  isLoading = false,
  mode = 'PARTIAL',
  fixedAmountCents
}) => {
  const [amount, setAmount] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      if (fixedAmountCents != null) {
        setAmount((fixedAmountCents / 100).toFixed(2))
      } else {
        setAmount('')
      }
      setError(null)
    }
  }, [isOpen, fixedAmountCents])

  const formatPrice = (cents: number) => {
    return `€${(cents / 100).toFixed(2)}`
  }

  const handleAmountChange = (value: string) => {
    // Remove any non-numeric characters except decimal point
    const cleaned = value.replace(/[^\d.]/g, '')
    
    // Only allow one decimal point
    const parts = cleaned.split('.')
    if (parts.length > 2) {
      return
    }
    
    // Limit to 2 decimal places
    if (parts[1] && parts[1].length > 2) {
      return
    }
    
    setAmount(cleaned)
    setError(null)
  }

  const handleConfirm = async () => {
    setError(null)

    // Full refund: fixed, non-editable amount (100% of order or provided fixed amount)
    if (mode === 'FULL') {
      const amountCents = fixedAmountCents ?? orderTotalCents

      if (!amountCents || amountCents <= 0) {
        setError('Invalid refund amount')
        return
      }

      try {
        await onConfirm(amountCents)
        onClose()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to process refund')
      }
      return
    }

    // Partial refund flow (editable amount with validation)
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid refund amount')
      return
    }

    const amountCents = Math.round(parseFloat(amount) * 100)
    
    if (amountCents > maxRefundCents) {
      setError(`Refund amount cannot exceed ${formatPrice(maxRefundCents)} (50% of order total)`)
      return
    }

    if (amountCents > 30000) {
      setError('Refund amount cannot exceed €300.00')
      return
    }

    if (amountCents > orderTotalCents) {
      setError('Refund amount cannot exceed the order total')
      return
    }

    try {
      await onConfirm(amountCents)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process refund')
    }
  }

  const handleQuickSelect = (percentage: number) => {
    const maxAmount = Math.min(maxRefundCents, orderTotalCents)
    const selectedAmount = Math.floor((maxAmount * percentage) / 100)
    setAmount((selectedAmount / 100).toFixed(2))
    setError(null)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {mode === 'FULL' ? 'Process Full Refund' : 'Process Partial Refund'}
              </h3>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
                disabled={isLoading}
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              {mode === 'PARTIAL' && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                    <div className="ml-3">
                      <p className="text-sm text-yellow-800">
                        Partial refunds can only be processed once per order and cannot exceed 50% of the order total.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Order Total
                  </label>
                  <p className="text-lg font-semibold text-gray-900">{formatPrice(orderTotalCents)}</p>
                </div>

                {mode === 'PARTIAL' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Maximum Refund (50% of order)
                      </label>
                      <p className="text-sm text-gray-600">{formatPrice(maxRefundCents)}</p>
                    </div>

                    <div>
                      <label htmlFor="refund-amount" className="block text-sm font-medium text-gray-700 mb-2">
                        Refund Amount <span className="text-red-500">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <span className="text-gray-500 sm:text-sm">€</span>
                        </div>
                        <input
                          type="text"
                          id="refund-amount"
                          value={amount}
                          onChange={(e) => handleAmountChange(e.target.value)}
                          placeholder="0.00"
                          className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                          disabled={isLoading}
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Quick Select
                      </label>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          type="button"
                          onClick={() => handleQuickSelect(25)}
                          disabled={isLoading}
                          className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                        >
                          25%
                        </button>
                        <button
                          type="button"
                          onClick={() => handleQuickSelect(50)}
                          disabled={isLoading}
                          className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                        >
                          50%
                        </button>
                        <button
                          type="button"
                          onClick={() => setAmount((maxRefundCents / 100).toFixed(2))}
                          disabled={isLoading}
                          className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                        >
                          Max
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {mode === 'FULL' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Refund Amount (100% of order)
                    </label>
                    <p className="text-lg font-semibold text-red-600">
                      {formatPrice(fixedAmountCents ?? orderTotalCents)}
                    </p>
                    <p className="mt-1 text-xs text-gray-500">
                      This will fully refund the order. The backend will validate permissions and limits.
                    </p>
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={
                isLoading ||
                (mode !== 'FULL' && (!amount || parseFloat(amount) <= 0))
              }
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Processing...' : 'Confirm Refund'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default RefundModal



